// UltraFlex Admin — Knowledge Base
// Presents the full library of training manuals with search / filter / download.
import React, { useState, useEffect, useMemo } from 'react';
import {
  Book, Search, Download, Filter, ExternalLink, ShieldCheck,
  FileText, ChevronDown, ChevronRight
} from 'lucide-react';

const KnowledgeBase = () => {
  const [manifest, setManifest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [openGroups, setOpenGroups] = useState({});

  useEffect(() => {
    // manifest is served from the public /training/ directory
    fetch('/training/manifest.json')
      .then((r) => {
        if (!r.ok) throw new Error(`Manifest not found (${r.status})`);
        return r.json();
      })
      .then((data) => {
        setManifest(data);
        // open all groups by default
        const opens = {};
        (data.manuals || []).forEach((m) => { opens[m.module] = true; });
        setOpenGroups(opens);
        setLoading(false);
      })
      .catch((e) => {
        setError(e.message);
        setLoading(false);
      });
  }, []);

  const grouped = useMemo(() => {
    if (!manifest) return [];
    const g = {};
    (manifest.manuals || []).forEach((m) => {
      const hitQ = !q ||
        m.name.toLowerCase().includes(q.toLowerCase()) ||
        m.module.toLowerCase().includes(q.toLowerCase()) ||
        m.roles.toLowerCase().includes(q.toLowerCase()) ||
        m.ref.toLowerCase().includes(q.toLowerCase());
      const hitP = priorityFilter === 'ALL' || m.priority === priorityFilter;
      const hitS = statusFilter === 'ALL' ||
        (statusFilter === 'LIVE' && m.platform_status.startsWith('LIVE') && !m.platform_status.includes('MOCKED')) ||
        (statusFilter === 'MOCKED' && m.platform_status.includes('MOCKED')) ||
        (statusFilter === 'PARTIAL' && m.platform_status.includes('PARTIAL'));
      if (hitQ && hitP && hitS) {
        (g[m.module] = g[m.module] || []).push(m);
      }
    });
    return Object.entries(g).sort((a, b) => a[0].localeCompare(b[0]));
  }, [manifest, q, priorityFilter, statusFilter]);

  const total = manifest?.manuals?.length || 0;
  const shown = grouped.reduce((n, [, arr]) => n + arr.length, 0);

  const priorityPill = (p) => {
    const map = {
      P0: 'bg-red-600/20 text-red-400 border-red-600/40',
      P1: 'bg-amber-600/20 text-amber-400 border-amber-600/40',
      P2: 'bg-yellow-600/20 text-yellow-400 border-yellow-600/40',
    };
    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold border ${map[p] || 'bg-slate-600/20 text-slate-400 border-slate-600/40'}`}
        data-testid={`kb-priority-${p}`}
      >
        {p}
      </span>
    );
  };

  const statusPill = (s) => {
    const live = s.startsWith('LIVE') && !s.includes('MOCKED') && !s.includes('PARTIAL');
    const mocked = s.includes('MOCKED');
    const partial = s.includes('PARTIAL');
    const cls = live
      ? 'bg-emerald-600/20 text-emerald-400 border-emerald-600/40'
      : mocked
      ? 'bg-rose-600/20 text-rose-400 border-rose-600/40'
      : partial
      ? 'bg-orange-600/20 text-orange-400 border-orange-600/40'
      : 'bg-slate-600/20 text-slate-400 border-slate-600/40';
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium border ${cls}`}>
        {s}
      </span>
    );
  };

  const toggle = (mod) =>
    setOpenGroups((g) => ({ ...g, [mod]: !g[mod] }));

  if (loading) {
    return (
      <div className="p-8" data-testid="kb-loading">
        <div className="animate-pulse text-uf-text-muted">Loading training library…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-8" data-testid="kb-error">
        <div className="bg-rose-950/40 border border-rose-800 text-rose-300 rounded-lg p-6">
          <p className="font-semibold mb-1">Could not load the training manifest.</p>
          <p className="text-sm opacity-80">{error}</p>
          <p className="text-sm mt-2">
            The manifest is generated by <code>/app/scripts/training/build_all_manuals.py</code>.
            Re-run that script if the file is missing.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="kb-root">
      {/* -------------------------------------------------- header */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-uf-red text-xs font-semibold tracking-widest uppercase">
            <Book className="w-4 h-4" /> Knowledge Base
          </div>
          <h1 className="text-3xl lg:text-4xl font-bold text-uf-text mt-1">
            Training Manuals
          </h1>
          <p className="text-uf-text-muted text-sm mt-2 max-w-3xl">
            The complete library of process training documentation for the UltraFlex
            Integrated Platform. Every manual is a printable PDF covering the
            end-to-end process with screen references, permissions, expected
            outcomes and troubleshooting.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <a
            href="/training/UltraFlex_Training_Manuals_Complete.zip"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-uf-red text-white text-sm font-semibold hover:bg-red-700 transition"
            data-testid="kb-download-all-zip"
            download
          >
            <Download className="w-4 h-4" />
            Download complete pack (ZIP)
          </a>
          <a
            href="/training/UltraFlex_Training_Manual_Index_v1.0.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-uf-surface-hi border border-uf-border text-uf-text text-sm font-medium hover:bg-uf-surface transition"
            data-testid="kb-download-index"
          >
            <FileText className="w-4 h-4" />
            Master Index PDF
          </a>
        </div>
      </div>

      {/* -------------------------------------------------- companion docs strip */}
      {manifest.companion_documents && manifest.companion_documents.length > 0 && (
        <div
          className="bg-uf-surface border border-uf-border rounded-lg p-4"
          data-testid="kb-companions"
        >
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-uf-text font-semibold text-sm">
                Companion documents
              </div>
              <div className="text-uf-text-muted text-xs">
                Suite v{manifest.suite_version || '1.0'} — read alongside the manuals
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            {manifest.companion_documents.map((c) => (
              <a
                key={c.file}
                href={`/training/${c.file}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-2 p-3 rounded-md bg-uf-surface-hi border border-uf-border hover:border-uf-red hover:bg-uf-surface transition group"
                data-testid={`kb-companion-${c.file.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`}
              >
                <FileText className="w-4 h-4 text-uf-red shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <div className="text-uf-text text-xs font-medium truncate group-hover:text-uf-red">
                    {c.label}
                  </div>
                  <div className="text-uf-text-muted text-[10px] mt-0.5">
                    {c.size_kb} KB
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* -------------------------------------------------- stats strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: 'Total Manuals', value: total, testid: 'kb-stat-total' },
          {
            label: 'P0 Critical',
            value: (manifest.manuals || []).filter((m) => m.priority === 'P0').length,
            testid: 'kb-stat-p0',
          },
          {
            label: 'LIVE Processes',
            value: (manifest.manuals || []).filter((m) =>
              m.platform_status.startsWith('LIVE') && !m.platform_status.includes('MOCKED')
            ).length,
            testid: 'kb-stat-live',
          },
          {
            label: 'MOCKED / PARTIAL',
            value: (manifest.manuals || []).filter((m) =>
              m.platform_status.includes('MOCKED') || m.platform_status.includes('PARTIAL')
            ).length,
            testid: 'kb-stat-mocked',
          },
        ].map((s) => (
          <div
            key={s.label}
            className="bg-uf-surface border border-uf-border rounded-lg p-4"
            data-testid={s.testid}
          >
            <div className="text-uf-text-muted text-xs uppercase tracking-wide">
              {s.label}
            </div>
            <div className="text-uf-text text-2xl font-bold mt-1">{s.value}</div>
          </div>
        ))}
      </div>

      {/* -------------------------------------------------- filters */}
      <div className="bg-uf-surface border border-uf-border rounded-lg p-4 flex flex-col lg:flex-row gap-3 items-stretch lg:items-center">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-uf-text-muted" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by title, module, role or ref (e.g. M04.3)"
            className="w-full bg-uf-surface-hi border border-uf-border rounded-lg pl-10 pr-3 py-2 text-sm text-uf-text placeholder-uf-text-muted focus:outline-none focus:ring-1 focus:ring-uf-red"
            data-testid="kb-search-input"
          />
        </div>
        <div className="flex gap-2">
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-sm text-uf-text"
            data-testid="kb-filter-priority"
          >
            <option value="ALL">All priorities</option>
            <option value="P0">P0 · Critical</option>
            <option value="P1">P1 · Important</option>
            <option value="P2">P2 · Standard</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-sm text-uf-text"
            data-testid="kb-filter-status"
          >
            <option value="ALL">All statuses</option>
            <option value="LIVE">LIVE only</option>
            <option value="PARTIAL">PARTIAL</option>
            <option value="MOCKED">MOCKED</option>
          </select>
        </div>
        <div className="text-xs text-uf-text-muted whitespace-nowrap">
          Showing <span className="text-uf-text font-medium">{shown}</span> of {total}
        </div>
      </div>

      {/* -------------------------------------------------- grouped list */}
      <div className="space-y-4">
        {grouped.map(([module, items]) => (
          <div
            key={module}
            className="bg-uf-surface border border-uf-border rounded-lg overflow-hidden"
            data-testid={`kb-group-${module.replace(/\s+/g, '-').toLowerCase()}`}
          >
            <button
              onClick={() => toggle(module)}
              className="w-full flex items-center justify-between px-5 py-3 hover:bg-uf-surface-hi transition text-left"
              data-testid={`kb-toggle-${module.replace(/\s+/g, '-').toLowerCase()}`}
            >
              <div className="flex items-center gap-3">
                {openGroups[module] ? (
                  <ChevronDown className="w-4 h-4 text-uf-text-muted" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-uf-text-muted" />
                )}
                <span className="text-uf-text font-semibold">{module}</span>
                <span className="text-xs text-uf-text-muted">
                  ({items.length} manual{items.length === 1 ? '' : 's'})
                </span>
              </div>
              <ShieldCheck className="w-4 h-4 text-emerald-500/70" />
            </button>
            {openGroups[module] && (
              <div className="divide-y divide-uf-border">
                {items.map((m) => (
                  <div
                    key={m.ref}
                    className="flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-6 px-5 py-4 hover:bg-uf-surface-hi transition"
                    data-testid={`kb-row-${m.ref.replace('.', '-')}`}
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="text-uf-red text-xs font-mono font-bold w-14 shrink-0">
                        {m.ref}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-uf-text font-medium truncate">
                          {m.name}
                        </div>
                        <div className="text-uf-text-muted text-xs mt-0.5 truncate">
                          Roles: {m.roles}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {priorityPill(m.priority)}
                      {statusPill(m.platform_status)}
                      <span className="text-uf-text-muted text-xs">
                        {m.size_kb} KB
                      </span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <a
                        href={`/training/${m.file}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-uf-surface-hi border border-uf-border text-uf-text text-xs font-medium hover:bg-uf-red hover:border-uf-red hover:text-white transition"
                        data-testid={`kb-view-${m.ref.replace('.', '-')}`}
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                        View
                      </a>
                      <a
                        href={`/training/${m.file}`}
                        download
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-uf-red text-white text-xs font-medium hover:bg-red-700 transition"
                        data-testid={`kb-download-${m.ref.replace('.', '-')}`}
                      >
                        <Download className="w-3.5 h-3.5" />
                        PDF
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        {grouped.length === 0 && (
          <div
            className="bg-uf-surface border border-uf-border rounded-lg p-10 text-center text-uf-text-muted"
            data-testid="kb-empty"
          >
            <Filter className="w-8 h-8 mx-auto mb-2 opacity-40" />
            No manuals match your filters. Try clearing the search or filter selection.
          </div>
        )}
      </div>

      {/* -------------------------------------------------- footer */}
      <div className="bg-uf-surface border border-uf-border rounded-lg p-4 flex items-start gap-3 text-xs text-uf-text-muted">
        <ShieldCheck className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
        <div>
          <b className="text-uf-text">Confidential — for authorised UltraFlex personnel only.</b>{' '}
          These training manuals contain operational, financial and personal-data
          handling procedures. Do not distribute externally. Rebuild the library
          any time by running{' '}
          <code className="bg-uf-surface-hi px-1 py-0.5 rounded text-uf-red">
            python3 /app/scripts/training/build_all_manuals.py
          </code>.
        </div>
      </div>
    </div>
  );
};

export default KnowledgeBase;
